import { useEffect, useRef, useState } from 'react';
import { ImageUp, Loader2, PersonStanding, Save } from 'lucide-react';
import type { VirtualUser } from '../../api/virtualUser';
import {
  EMPTY_DIMS,
  getBodyProfile,
  updateBodyProfile,
  uploadBodyImage,
  type BodyDims,
  type BodyProfile,
} from '../../api/bodyProfile';
import { VirtualUserSelect } from '../shared/VirtualUserSelect';
import { BodySilhouette } from './BodySilhouette';

interface Props {
  virtualUsers: VirtualUser[];
  selected: VirtualUser | null;
  onSelect: (u: VirtualUser) => void;
}

const BODY_SHAPES = [
  { value: 'hourglass', label: '沙漏' },
  { value: 'pear', label: '梨形' },
  { value: 'apple', label: '苹果' },
  { value: 'rectangle', label: '矩形' },
  { value: 'inverted_triangle', label: '倒三角' },
];

const DIM_FIELDS: { key: keyof BodyDims; label: string }[] = [
  { key: 'shoulder', label: '肩宽' },
  { key: 'bust', label: '胸围感' },
  { key: 'waist', label: '腰围感' },
  { key: 'hip', label: '臀围感' },
  { key: 'leg_length', label: '腿长比' },
  { key: 'proportion', label: '整体比例' },
];

export function BodyPage({ virtualUsers, selected, onSelect }: Props) {
  const [profile, setProfile] = useState<BodyProfile | null>(null);
  const [dims, setDims] = useState<BodyDims>(EMPTY_DIMS);
  const [heightCm, setHeightCm] = useState(170);
  const [weightKg, setWeightKg] = useState(55);
  const [gender, setGender] = useState('woman');
  const [bodyShape, setBodyShape] = useState('hourglass');
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!selected) {
      setProfile(null);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      setMsg(null);
      try {
        const p = await getBodyProfile(selected.id);
        if (cancelled) return;
        setProfile(p);
        setDims(p.body_dims ?? EMPTY_DIMS);
        setHeightCm(p.height_cm ?? 170);
        setWeightKg(p.weight_kg ?? 55);
        setGender(p.gender || 'woman');
        setBodyShape(p.body_shape || 'hourglass');
        setContent(p.content || '');
      } catch (e) {
        if (!cancelled) setMsg(e instanceof Error ? e.message : '加载人模失败');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selected?.id]);

  const liveProfile = {
    height_cm: heightCm,
    weight_kg: weightKg,
    body_dims: dims,
  };

  const save = async () => {
    if (!selected) return;
    setSaving(true);
    setMsg(null);
    try {
      const p = await updateBodyProfile(selected.id, {
        gender,
        height_cm: heightCm,
        weight_kg: weightKg,
        body_shape: bodyShape,
        content,
        body_dims: dims,
      });
      setProfile(p);
      setMsg('已写回 user_profiles + profile_json');
    } catch (e) {
      setMsg(e instanceof Error ? e.message : '保存失败');
    } finally {
      setSaving(false);
    }
  };

  const onPickImage = async (file: File | null) => {
    if (!selected || !file) return;
    setUploading(true);
    setMsg(null);
    try {
      const p = await uploadBodyImage(selected.id, file);
      setProfile(p);
      setMsg('人模图已上传并写回 body_model');
    } catch (e) {
      setMsg(e instanceof Error ? e.message : '上传失败');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  return (
    <div className="h-full flex flex-col">
      <div className="h-12 border-b border-[var(--line)] bg-[var(--paper)] px-4 flex items-center gap-4 shrink-0">
        <div className="flex items-center gap-2 text-sm font-medium">
          <PersonStanding className="w-4 h-4" />
          捏人模
        </div>
        <VirtualUserSelect virtualUsers={virtualUsers} selected={selected} onSelect={onSelect} />
        <input
          ref={fileRef}
          type="file"
          accept="image/png,image/jpeg,image/webp"
          className="hidden"
          onChange={(e) => onPickImage(e.target.files?.[0] ?? null)}
        />
        <button
          className="btn-ghost text-sm ml-auto"
          disabled={uploading || !selected}
          onClick={() => fileRef.current?.click()}
        >
          {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImageUp className="w-4 h-4" />}
          换图上传
        </button>
        <button className="btn-primary text-sm" disabled={saving || !selected} onClick={save}>
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          保存并写回
        </button>
      </div>

      {loading ? (
        <div className="flex-1 flex items-center justify-center text-[var(--muted)]">
          <Loader2 className="w-5 h-5 animate-spin mr-2" />
          加载人模...
        </div>
      ) : (
        <div className="flex-1 min-h-0 grid grid-cols-1 lg:grid-cols-[1fr_380px]">
          <div className="min-h-0 flex items-end justify-center gap-6 bg-[linear-gradient(180deg,#f3f3f5,transparent)] p-6">
            <div className="relative h-[min(72vh,640px)] w-[min(280px,40%)] flex items-end justify-center">
              <BodySilhouette profile={liveProfile} className="h-full w-auto transition-all duration-150" />
            </div>
            {profile?.body_thumb && (
              <div className="h-[min(72vh,640px)] w-[min(220px,28%)] flex flex-col items-center justify-end gap-2">
                <img
                  src={profile.body_thumb}
                  alt="body model"
                  className="max-h-[calc(100%-1.5rem)] max-w-full object-contain"
                  draggable={false}
                />
                <div className="text-[10px] text-[var(--muted)]">原图（不随局部滑杆变形）</div>
              </div>
            )}
          </div>

          <aside className="min-h-0 overflow-y-auto border-l border-[var(--line)] bg-[var(--paper)] p-4 space-y-5 scrollbar-thin">
            <div className="text-xs text-[var(--muted)]">
              肩宽、胸围、腰围、臀围只改变对应部位；身高拉整体高度，腿长比只拉长腿。原图保持不变。
              {profile?.body_model_id ? ` · body_model ${profile.body_model_id.slice(0, 8)}…` : ' · 暂无 body_model 图'}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <label className="text-xs text-[var(--muted)]">
                性别
                <select
                  className="mt-1 w-full text-sm border border-[var(--line)] rounded px-2 py-1.5"
                  value={gender}
                  onChange={(e) => setGender(e.target.value)}
                >
                  <option value="woman">女</option>
                  <option value="man">男</option>
                </select>
              </label>
              <label className="text-xs text-[var(--muted)]">
                体型
                <select
                  className="mt-1 w-full text-sm border border-[var(--line)] rounded px-2 py-1.5"
                  value={bodyShape}
                  onChange={(e) => setBodyShape(e.target.value)}
                >
                  {BODY_SHAPES.map((s) => (
                    <option key={s.value} value={s.value}>
                      {s.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="text-xs text-[var(--muted)]">
                身高 cm
                <input
                  type="number"
                  className="mt-1 w-full text-sm border border-[var(--line)] rounded px-2 py-1.5"
                  value={heightCm}
                  min={140}
                  max={200}
                  onChange={(e) => setHeightCm(Number(e.target.value) || 170)}
                />
              </label>
              <label className="text-xs text-[var(--muted)]">
                体重 kg
                <input
                  type="number"
                  className="mt-1 w-full text-sm border border-[var(--line)] rounded px-2 py-1.5"
                  value={weightKg}
                  min={35}
                  max={120}
                  step={0.5}
                  onChange={(e) => setWeightKg(Number(e.target.value) || 55)}
                />
              </label>
            </div>

            <div className="space-y-3">
              <div className="text-xs font-medium text-[var(--ink)]">身材比例（写回 profile_json.body_dims）</div>
              {DIM_FIELDS.map((f) => (
                <label key={f.key} className="block text-xs text-[var(--muted)]">
                  <div className="flex justify-between mb-1">
                    <span>{f.label}</span>
                    <span>{dims[f.key].toFixed(2)}</span>
                  </div>
                  <input
                    type="range"
                    min={-1}
                    max={1}
                    step={0.01}
                    value={dims[f.key]}
                    onChange={(e) =>
                      setDims((prev) => ({ ...prev, [f.key]: Number(e.target.value) }))
                    }
                    className="w-full"
                  />
                </label>
              ))}
            </div>

            <label className="block text-xs text-[var(--muted)]">
              风格备注（content）
              <textarea
                className="mt-1 w-full text-sm border border-[var(--line)] rounded px-2 py-1.5 min-h-[72px]"
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder="用户需求 / 风格偏好"
              />
            </label>

            {msg && <div className="text-xs text-center text-[var(--muted)]">{msg}</div>}
          </aside>
        </div>
      )}
    </div>
  );
}
